import {useSnackbar} from "notistack";
import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import useApiFetch, {apiEndpoint} from "../Auth/Api";
import {
    EmployeeActuals,
    Project,
} from "../Scheduling/Utils/EmployeeSchedulesCompiler";

type ProjectsContextType = {
    activeProjects : Project[] | null;
    projectsWithAllStatuses : Project[] | null;
    loadAdditionalProjects : (projectIds : number[]) => Promise<Project[]>;
    detectAndLoadMissingProjects : (employeeActuals : EmployeeActuals[]) => void;
    refreshProjects : () => Promise<void>;
};

const ProjectsContext = createContext<ProjectsContextType | null>(null);

type Props = {
    children : ReactNode;
};

const ProjectsProvider : React.FC<Props> = ({children} : Props) => {
    const apiFetch = useApiFetch();
    const {enqueueSnackbar} = useSnackbar();
    const [projectsWithAllStatuses, setProjectsWithAllStatuses] = useState<Project[] | null>(null);
    const [activeProjects, setActiveProjects] = useState<Project[] | null>(null);

    const loadProjects = useCallback(async () => {
        const response = await apiFetch(
            new URL("/v1/scheduler/projects", apiEndpoint).toString(),
        );

        if (!response.ok) {
            enqueueSnackbar("Could not load projects", {variant: "error"});
            return;
        }

        const projectList = (await response.json()) as Project[];
        const sortedProjects = projectList.sort((a, b) => a.name.localeCompare(b.name));
        setProjectsWithAllStatuses(sortedProjects);
        setActiveProjects(sortedProjects);
    }, [apiFetch, enqueueSnackbar]);

    const loadAdditionalProjects = useCallback(
        async (projectIds : number[]) : Promise<Project[]> => {
            if (projectIds.length === 0 || !projectsWithAllStatuses) {
                return [];
            }

            const existingIds = new Set(projectsWithAllStatuses.map((p) => p.id));
            const missingIds = projectIds.filter((id) => !existingIds.has(id));

            if (missingIds.length === 0) {
                return projectsWithAllStatuses.filter((p) => projectIds.includes(p.id));
            }

            const url = new URL("/v1/scheduler/projects/by-ids", apiEndpoint);
            url.searchParams.set("ids", missingIds.join(","));

            const response = await apiFetch(url.toString());

            if (!response.ok) {
                enqueueSnackbar("Could not load additional projects", {
                    variant: "error",
                });
                return [];
            }

            const additionalProjects = (await response.json()) as Project[];

            setProjectsWithAllStatuses((prev) => {
                if (!prev) {
                    return additionalProjects.sort((a, b) =>
                        a.name.localeCompare(b.name),
                    );
                }
                return [...prev, ...additionalProjects].sort((a, b) =>
                    a.name.localeCompare(b.name),
                );
            });

            return additionalProjects;
        },
        [apiFetch, enqueueSnackbar, projectsWithAllStatuses],
    );

    const detectAndLoadMissingProjects = useCallback(
        (employeeActuals : EmployeeActuals[]) => {
            if (!projectsWithAllStatuses) {
                return;
            }

            const actualProjectIds = new Set<number>();
            for (const ea of employeeActuals) {
                for (const p of ea.projects) {
                    actualProjectIds.add(p.id);
                }
            }

            const existingProjectIds = new Set(projectsWithAllStatuses.map((p) => p.id));
            const missingIds = [...actualProjectIds].filter(
                (id) => !existingProjectIds.has(id),
            );

            if (missingIds.length > 0) {
                loadAdditionalProjects(missingIds);
            }
        },
        [projectsWithAllStatuses, loadAdditionalProjects],
    );

    const refreshProjects = useCallback(async () => {
        await loadProjects();
    }, [loadProjects]);

    useEffect(() => {
        void loadProjects();
    }, [loadProjects]);

    const value : ProjectsContextType = {
        activeProjects,
        projectsWithAllStatuses,
        loadAdditionalProjects,
        detectAndLoadMissingProjects,
        refreshProjects,
    };

    return (
        <ProjectsContext.Provider value={value}>
            {children}
        </ProjectsContext.Provider>
    );
};

export const useProjects = () : ProjectsContextType => {
    const context = useContext(ProjectsContext);

    if (!context) {
        throw new Error("useProjects must be used within a ProjectsProvider");
    }

    return context;
};


export default ProjectsProvider;
